import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Layers, RefreshCw, ChevronLeft, ChevronRight, RotateCcw, Lightbulb, ThumbsUp, ThumbsDown, Trophy, Shuffle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_CARDS_PER_SESSION = 30;  // Limit flashcards per session for focused learning

// Fisher-Yates shuffle
function shuffleArray(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export default function Flashcards() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const topics = searchParams.get('topics') || '';
  const difficulties = searchParams.get('difficulties') || '';
  const subtopics = searchParams.get('subtopics') || '';

  const [allCards, setAllCards] = useState([]);  // Full pool of cards
  const [cards, setCards] = useState([]);        // Current session cards (shuffled)
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [sessionSaved, setSessionSaved] = useState(false);
  
  // Session-based tracking to avoid repeats
  const usedCardIds = useRef(new Set());
  const [poolExhausted, setPoolExhausted] = useState(false);
  
  // Right/Wrong tracking
  const [correctCards, setCorrectCards] = useState(new Set());
  const [incorrectCards, setIncorrectCards] = useState(new Set());
  const [answeredCards, setAnsweredCards] = useState(new Set());

  const fetchCards = useCallback(async () => {
    try {
      const params = {
        game_type: 'flashcard',
        topics: topics || undefined,
        difficulties: difficulties || undefined,
        subtopics: subtopics || undefined,
        limit: 100  // Fetch larger pool for session tracking and variety
      };
      const res = await axios.get(`${BACKEND_URL}/api/games/questions`, { params, withCredentials: true });
      const gameCards = res.data.questions || [];
      
      if (gameCards.length === 0) {
        toast.error('No flashcards available');
      }
      
      setAllCards(gameCards);
      initializeRound(gameCards, new Set());
    } catch (err) {
      toast.error('Failed to load flashcards');
    } finally {
      setLoading(false);
    }
  }, [topics, difficulties, subtopics]);

  // Initialize a round with shuffled cards, excluding used ones
  // Limits to MAX_CARDS_PER_SESSION (30) cards for focused learning
  const initializeRound = (pool, usedIds) => {
    // Filter out already used cards
    const available = pool.filter(c => !usedIds.has(c.question_id));
    
    if (available.length === 0) {
      setPoolExhausted(true);
      return;
    }
    
    // Shuffle all available cards
    const shuffled = shuffleArray(available);
    
    // Take only up to MAX_CARDS_PER_SESSION cards
    const sessionCards = shuffled.slice(0, MAX_CARDS_PER_SESSION);
    
    // Mark these as used
    sessionCards.forEach(c => usedCardIds.current.add(c.question_id));
    
    setCards(sessionCards);
    
    // Start at a random card
    const randomStart = Math.floor(Math.random() * sessionCards.length);
    setCurrentIndex(randomStart);
    
    setIsFlipped(false);
    setCorrectCards(new Set());
    setIncorrectCards(new Set());
    setAnsweredCards(new Set());
    setSessionSaved(false);
    setPoolExhausted(false);
    setStartTime(Date.now());
  };

  // Handle "New Set" button - get different cards
  const handleNewSet = () => {
    if (poolExhausted) {
      // Reset session tracking and start fresh
      usedCardIds.current = new Set();
      initializeRound(allCards, new Set());
      toast.success('Pool reset! Starting fresh.');
    } else {
      initializeRound(allCards, usedCardIds.current);
    }
  };

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  // Save game session
  const saveGameSession = useCallback(async () => {
    if (sessionSaved || cards.length === 0) return;
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    try {
      await axios.post(`${BACKEND_URL}/api/games/session`, {
        game_type: 'flashcards',
        topic_key: topics || null,
        difficulty: difficulties || null,
        completed_items: answeredCards.size,
        revealed_items: 0,
        total_items: cards.length,
        correct_items: correctCards.size,
        time_seconds: elapsed
      }, { withCredentials: true });
      setSessionSaved(true);
    } catch (err) {
      console.error('Failed to save session');
    }
  }, [topics, difficulties, cards.length, correctCards.size, answeredCards.size, startTime, sessionSaved]);

  const handleMarkCorrect = () => {
    const cardId = cards[currentIndex]?.question_id;
    if (!cardId) return;
    
    setCorrectCards(prev => new Set([...prev, cardId]));
    setIncorrectCards(prev => {
      const next = new Set(prev);
      next.delete(cardId);
      return next;
    });
    setAnsweredCards(prev => new Set([...prev, cardId]));
    
    // Auto-advance to next card
    if (currentIndex < cards.length - 1) {
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setIsFlipped(false);
      }, 300);
    }
  };

  const handleMarkIncorrect = () => {
    const cardId = cards[currentIndex]?.question_id;
    if (!cardId) return;
    
    setIncorrectCards(prev => new Set([...prev, cardId]));
    setCorrectCards(prev => {
      const next = new Set(prev);
      next.delete(cardId);
      return next;
    });
    setAnsweredCards(prev => new Set([...prev, cardId]));
    
    // Auto-advance to next card
    if (currentIndex < cards.length - 1) {
      setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
        setIsFlipped(false);
      }, 300);
    }
  };

  const handleNext = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    }
  };

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setIsFlipped(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handleRestart = () => {
    // Restart current set with reshuffled cards
    const reshuffled = shuffleArray(cards);
    setCards(reshuffled);
    const randomStart = Math.floor(Math.random() * reshuffled.length);
    setCurrentIndex(randomStart);
    setIsFlipped(false);
    setCorrectCards(new Set());
    setIncorrectCards(new Set());
    setAnsweredCards(new Set());
    setSessionSaved(false);
    setStartTime(Date.now());
  };

  // Reset entire session (clear used tracking)
  const handleResetSession = () => {
    usedCardIds.current = new Set();
    setPoolExhausted(false);
    initializeRound(allCards, new Set());
    toast.success('Session reset! All cards available again.');
  };

  const handleReviewMissed = () => {
    // Filter to only show cards marked as incorrect
    const missedCards = cards.filter(c => incorrectCards.has(c.question_id));
    if (missedCards.length === 0) {
      toast.info('No missed cards to review!');
      return;
    }
    // Shuffle the missed cards and start at random position
    const shuffled = shuffleArray(missedCards);
    setCards(shuffled);
    const randomStart = Math.floor(Math.random() * shuffled.length);
    setCurrentIndex(randomStart);
    setIsFlipped(false);
    setCorrectCards(new Set());
    setIncorrectCards(new Set());
    setAnsweredCards(new Set());
    setSessionSaved(false);
    setStartTime(Date.now());
  };

  // Save session when all cards are reviewed
  useEffect(() => {
    if (answeredCards.size === cards.length && cards.length > 0 && !sessionSaved) {
      saveGameSession();
    }
  }, [answeredCards.size, cards.length, sessionSaved, saveGameSession]);

  const currentCard = cards[currentIndex];
  const progress = cards.length > 0 ? ((currentIndex + 1) / cards.length) * 100 : 0;
  const currentCardStatus = currentCard ? (
    correctCards.has(currentCard.question_id) ? 'correct' :
    incorrectCards.has(currentCard.question_id) ? 'incorrect' : null
  ) : null;
  const isComplete = answeredCards.size === cards.length && cards.length > 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (cards.length === 0 && !poolExhausted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Layers className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">No Flashcards Available</h2>
          <p className="text-slate-600 mb-4">No published flashcard game questions found</p>
          <Button onClick={() => navigate('/games/flashcards/setup')}>Change Filters</Button>
        </div>
      </div>
    );
  }

  // Pool exhausted state
  if (poolExhausted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-background flex items-center justify-center">
        <div className="text-center bg-white rounded-xl border border-slate-200 p-8 max-w-md">
          <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">All Cards Completed!</h2>
          <p className="text-slate-600 mb-6">You&apos;ve gone through all available flashcards for this topic.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={handleResetSession} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" /> Start Over
            </Button>
            <Button onClick={() => navigate('/games')} className="bg-blue-500 text-white">
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-background">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button onClick={() => navigate('/games/flashcards/setup')} variant="ghost" className="text-slate-600">
              <ArrowLeft className="w-4 h-4 mr-2" /> Exit
            </Button>
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-500" />
              <span className="font-semibold text-slate-900">Flashcards</span>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleNewSet} variant="ghost" className="text-blue-600 hover:bg-blue-50">
                <Shuffle className="w-4 h-4 mr-2" /> New Set
              </Button>
              <Button onClick={handleRestart} variant="ghost" className="text-slate-600">
                <RefreshCw className="w-4 h-4 mr-2" /> Restart
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-8">
        {/* Progress & Stats */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-slate-600 mb-2">
            <span>Card {currentIndex + 1} of {cards.length}</span>
            <div className="flex gap-4">
              <span className="text-emerald-600">✓ {correctCards.size}</span>
              <span className="text-red-500">✗ {incorrectCards.size}</span>
            </div>
          </div>
          <div className="w-full h-2 bg-blue-100 rounded-full">
            <div className="h-full bg-blue-500 rounded-full transition-all" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-xs text-slate-400 mt-1 text-center">
            Cards used this session: {usedCardIds.current.size} / {allCards.length}
          </p>
        </div>

        {/* Topic/Subtopic Badge */}
        {currentCard && (
          <div className="flex gap-2 mb-4 justify-center flex-wrap">
            <span className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
              {currentCard.topic}
            </span>
            {currentCard.subtopic && (
              <span className="text-xs px-3 py-1 bg-slate-100 text-slate-600 rounded-full">
                {currentCard.subtopic}
              </span>
            )}
            {currentCard.difficulty && (
              <span className="text-xs px-3 py-1 bg-amber-100 text-amber-700 rounded-full">
                {currentCard.difficulty}
              </span>
            )}
            {currentCardStatus === 'correct' && (
              <span className="text-xs px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                ✓ Marked Correct
              </span>
            )}
            {currentCardStatus === 'incorrect' && (
              <span className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-full">
                ✗ Needs Review
              </span>
            )}
          </div>
        )}

        {/* Card */}
        <div 
          onClick={handleFlip}
          className="relative cursor-pointer perspective-1000"
          style={{ minHeight: '360px' }}
        >
          <div 
            className={`absolute inset-0 transition-transform duration-500 transform-style-preserve-3d ${
              isFlipped ? 'rotate-y-180' : ''
            }`}
            style={{ 
              transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
            }}
          >
            {/* Front - Prompt */}
            <div 
              className="absolute inset-0 bg-white rounded-2xl border border-slate-200 shadow-lg p-8 flex flex-col items-center justify-center backface-hidden"
              style={{ backfaceVisibility: 'hidden' }}
            >
              <p className="text-xs uppercase tracking-wide text-blue-600 mb-4">Question</p>
              <p className="text-xl text-slate-900 text-center leading-relaxed">
                {currentCard?.prompt}
              </p>
              <p className="text-sm text-slate-400 mt-6">Click to reveal answer</p>
            </div>

            {/* Back - Answer + Rationale */}
            <div 
              className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-8 flex flex-col items-center justify-center backface-hidden"
              style={{ 
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)'
              }}
            >
              <p className="text-xs uppercase tracking-wide text-blue-200 mb-3">Answer</p>
              <p className="text-2xl font-bold text-white text-center mb-6">
                {currentCard?.correct_answer}
              </p>
              
              {currentCard?.rationale && (
                <div className="bg-white/10 rounded-lg p-4 w-full">
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-amber-300" />
                    <p className="text-xs uppercase tracking-wide text-blue-200">Rationale</p>
                  </div>
                  <p className="text-sm text-blue-100 leading-relaxed">
                    {currentCard.rationale}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right/Wrong Buttons (show when flipped) */}
        {isFlipped && !isComplete && (
          <div className="flex gap-4 justify-center mt-6">
            <Button
              onClick={handleMarkIncorrect}
              variant="outline"
              className="flex-1 max-w-[150px] gap-2 border-red-300 text-red-600 hover:bg-red-50"
            >
              <ThumbsDown className="w-4 h-4" /> I Missed It
            </Button>
            <Button
              onClick={handleMarkCorrect}
              className="flex-1 max-w-[150px] gap-2 bg-emerald-500 hover:bg-emerald-600 text-white"
            >
              <ThumbsUp className="w-4 h-4" /> Got It!
            </Button>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <Button 
            onClick={handlePrev} 
            disabled={currentIndex === 0}
            variant="outline"
            className="gap-2"
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </Button>
          
          <button
            onClick={handleFlip}
            className="p-3 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          
          <Button 
            onClick={handleNext} 
            disabled={currentIndex === cards.length - 1}
            className="gap-2 bg-blue-500 hover:bg-blue-600 text-white"
          >
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Completion Summary */}
        {isComplete && (
          <div className="mt-8 bg-white rounded-xl border border-slate-200 p-8 text-center">
            <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-4">Session Complete!</h3>
            
            <div className="grid grid-cols-2 gap-4 mb-6 max-w-xs mx-auto">
              <div className="bg-emerald-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-emerald-600">{correctCards.size}</div>
                <div className="text-sm text-emerald-700">Got It Right</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4">
                <div className="text-3xl font-bold text-red-500">{incorrectCards.size}</div>
                <div className="text-sm text-red-600">Missed</div>
              </div>
            </div>
            
            <p className="text-slate-600 mb-6">
              {correctCards.size === cards.length 
                ? 'Perfect score! You got all cards correct!'
                : `Accuracy: ${Math.round((correctCards.size / cards.length) * 100)}%`
              }
            </p>
            
            <p className="text-sm text-slate-500 mb-4">
              {allCards.length - usedCardIds.current.size > 0 
                ? `${allCards.length - usedCardIds.current.size} more cards available`
                : 'All cards in pool completed!'
              }
            </p>
            
            <div className="flex gap-3 justify-center flex-wrap">
              {incorrectCards.size > 0 && (
                <Button onClick={handleReviewMissed} variant="outline" className="gap-2">
                  <RefreshCw className="w-4 h-4" /> Review Missed ({incorrectCards.size})
                </Button>
              )}
              {allCards.length - usedCardIds.current.size > 0 && (
                <Button onClick={handleNewSet} className="bg-blue-500 hover:bg-blue-600 text-white gap-2">
                  <Shuffle className="w-4 h-4" /> New Set
                </Button>
              )}
              <Button onClick={handleRestart} variant="outline">
                Start Over (Same Set)
              </Button>
              <Button onClick={() => navigate('/games')} variant="outline">
                Back to Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
